import { Injectable } from '@angular/core';
import { StationModel } from '../models/station-model.model';
import { TranslateService } from '@ngx-translate/core';
import { Station } from '../models/station.model';
import { GroupModel } from '../models/group-model.model';
import { ConnectorModel } from '../models/connector-model.model';
import { PaymentHistory } from '../models/payment-history.model';
import { PaymentHistoryModel } from '../models/payment-history-model.model';
import { StationOwnerHistory } from '../models/station-owner-history.model';
import { StationOwnerHistoryModal } from '../models/station-owner-history-model.model';
import { DriverHistory } from '../models/driver-history.model';
import { DriverHistoryModel } from '../models/driver-history-model.model';
import '../shared/compatibility';
import { OcppLogModel } from '../models/ocpp-log-model.model';
import { OcppLog } from '../models/ocpp-log.model';
import { JsonSerializer } from 'typescript-json-serializer';
import { OcmfTransaction } from '../models/ocmf-transaction.model';
import { StationGroup } from '../models/station-group';

@Injectable()
export class ModelHelperService {
  private jsonSerializer = new JsonSerializer();

  constructor(public translate: TranslateService) { }

  getStationModels(stations: Station[]): StationModel[] {
    const models = new Array<StationModel>();

    stations.forEach((el) => {
      const model = new StationModel(el, this.translate);

      models.push(model);
    });

    return models;
  }

  getStationModelsAndConnectorModelsWithGroups(
    stations: Station[]
  ): Array<StationModel | ConnectorModel | GroupModel> {
    const singleStations = new Array<Station>();
    const stationsWithGroups = new Array<Station>();
    const unlicensedStations = new Array<Station>();

    const unlicensedStationsGroup = new StationGroup();
    const unlicensedStationsGroupId = 'unlicensed-stations';
    unlicensedStationsGroup['Id'] = unlicensedStationsGroupId;
    unlicensedStationsGroup['Name'] = this.translate.instant(
      'stations.unlicensed-group-title'
    );

    // Divide stations into two groups - stations that are part of the group and single stations
    stations.forEach((s) => {
      if (s.IsDisabled) {
        unlicensedStations.push(s);
        s.StationGroups = [unlicensedStationsGroup];
      }

      if (!s.StationGroups || s.StationGroups.length === 0) {
        singleStations.push(s);
      } else {
        stationsWithGroups.push(s);
      }
    });

    // Create view models for stations that do not belong to any groups
    const viewModels: Array<
      StationModel | ConnectorModel | GroupModel
    > = singleStations.flatMap((s) => {
      return this.viewModelsForStation(s);
    });

    // TODO: The fields cannot be accessed directly because the object is incorrectly serialized in the legacy code.
    // Fix this by implementing proper serialization in the stations service.
    // I had to fallback to ['...'] accessor.

    // Create view models for stations that are grouped
    // Extract all station groups, remove duplicates, sort by name
    let allStationGroups = stationsWithGroups.flatMap((s) => s.StationGroups);
    allStationGroups = allStationGroups.filter(
      (group, index, self) =>
        self.findIndex((g) => g['Id'] === group['Id']) === index
    );
    allStationGroups = allStationGroups.sort((a, b) => {
      const isAUnlicensed = a['Id'] === unlicensedStationsGroupId;
      const isBUnlicensed = b['Id'] === unlicensedStationsGroupId;

      if (isAUnlicensed && !isBUnlicensed) {
        return 1;
      }

      if (!isAUnlicensed && isBUnlicensed) {
        return -1;
      }
      return a['Name'].localeCompare(b['Name']);
    });

    const groupRelatedViewModels = allStationGroups.flatMap((group) => {
      let results = new Array<StationModel | ConnectorModel | GroupModel>();

      // Group
      const groupViewModel = new GroupModel();
      groupViewModel.Name = group['Name'];
      groupViewModel.Id = group['Id'];
      results.push(groupViewModel);

      // Stations
      const stationsInTheGroup = stationsWithGroups.filter((s) =>
        s.StationGroups.some((g) => g['Id'] === group['Id'])
      );
      groupViewModel.Models = stationsInTheGroup.map((s) => {
        const stationModel = new StationModel(s, this.translate);
        stationModel.GroupModel = groupViewModel;
        return stationModel;
      });
      results = results.concat(
        stationsInTheGroup.flatMap((s) =>
          this.viewModelsForStation(s, groupViewModel)
        )
      );

      return results;
    });

    return viewModels.concat(groupRelatedViewModels);
  }

  private viewModelsForStation(
    station: Station,
    stationGroupModel: GroupModel = null
  ): Array<StationModel | ConnectorModel | GroupModel> {
    const stationModel = new StationModel(station, this.translate);
    stationModel.GroupModel = stationGroupModel;

    let models = new Array<StationModel | ConnectorModel>(stationModel);

    if (stationModel.Connectors.length > 1) {
      models = models.concat(stationModel.Connectors);
    }

    return models;
  }

  getPaymentHistoryModels(
    paymentHistories: PaymentHistory[]
  ): PaymentHistoryModel[] {
    const models = new Array<PaymentHistoryModel>();

    paymentHistories.forEach((el) => {
      const model = new PaymentHistoryModel(el, this.translate);

      models.push(model);
    });

    return models;
  }

  getDriverHistoryModels(
    driverHistories: DriverHistory[]
  ): DriverHistoryModel[] {
    const models = new Array<DriverHistoryModel>();

    driverHistories.forEach((el) => {
      const model = new DriverHistoryModel(el);

      this.setupOcmfInformation(model);

      models.push(model);
    });

    return models;
  }

  getStationOwnwerHistoryModals(
    paymentHistories: StationOwnerHistory[]
  ): StationOwnerHistoryModal[] {
    const models = new Array<StationOwnerHistoryModal>();

    paymentHistories.forEach((el) => {
      const model = new StationOwnerHistoryModal(el, this.translate);

      models.push(model);
    });

    return models;
  }

  getOcppLogModels(ocppLogs: OcppLog[]): OcppLogModel[] {
    const models = new Array<OcppLogModel>();

    ocppLogs.forEach((el) => {
      const model = new OcppLogModel(el);

      models.push(model);
    });

    return models;
  }

  private setupOcmfInformation(model: DriverHistoryModel) {

    if (model.SignedTransaction) {
      var xmlSplit = model.SignedTransaction.split("OCMF|");

      if (xmlSplit.length >= 2) {
        var splitedTransaction = xmlSplit[1].split("|");
        if (splitedTransaction.length >= 1) {
          const transaction = this.jsonSerializer.deserializeObject<OcmfTransaction>(
            JSON.parse(splitedTransaction[0]),
            OcmfTransaction
          )
          model.TransactionId = transaction.identificationData;
          model.PaginationCounter = transaction.paginationCounter;
        }
      }
    }
  }
}